/**
 * Centralized application constants.
 * Replaces magic numbers scattered across components.
 */

export const APP_NAME = 'Guides Enhanced Answers';

/** Polling interval for whole-site delete status checks (ms) */
export const DELETE_POLL_INTERVAL_MS = 4000;

/** Polling interval for file/folder (node) delete job status checks (ms) */
export const NODE_DELETE_POLL_INTERVAL_MS = 4000;

/** Polling interval for indexing status checks (ms). Also the cadence of the
 * "Add content" history heartbeat (GET bulk-index/history) that keeps the full
 * job table -- including cross-user queued/running rows -- fresh. */
export const INDEX_POLL_INTERVAL_MS = 10000;

/**
 * Polling interval for single-file save-and-index status checks (ms).
 * Tighter than INDEX_POLL_INTERVAL_MS since a direct-mode single-file
 * job is expected to resolve in one or two ticks, not the minutes a
 * bulk job can take.
 */
export const SINGLE_FILE_INDEX_POLL_INTERVAL_MS = 1500;

/** Number of files to show per page in the indexing file table */
export const FILES_PER_PAGE = 50;

/** Number of documents per page in the site stats view */
export const DOCUMENTS_PAGE_SIZE = 20;

/** Number of conversion jobs fetched per page in the conversion history panel */
export const CONVERSION_HISTORY_PAGE_SIZE = 20;

/** Duration to show success messages before auto-dismiss (ms) */
export const SUCCESS_MESSAGE_TIMEOUT_MS = 4000;

/** Default settings tab shown when the user navigates to Settings. */
export const DEFAULT_SETTINGS_TAB = 'accessControl' as const;

/** Duration to show save success before auto-dismiss (ms) */
export const SAVE_SUCCESS_TIMEOUT_MS = 3000;

/** Terminal indexing job statuses */
export const TERMINAL_STATUSES: string[] = ['completed', 'failed', 'cancelled'];

/** Polling interval for conversion status checks (ms) */
export const CONVERSION_POLL_INTERVAL_MS = 5000;

/** Polling interval for entity-dedup job status checks (ms). Matches the GKG
 * runner's own data-plane poll cadence (ENTITY_DEDUP_POLL_INTERVAL default 10s). */
export const ENTITY_DEDUP_POLL_INTERVAL_MS = 10000;

/** Terminal entity-dedup job statuses — polling stops once the job reaches one. */
export const ENTITY_DEDUP_TERMINAL_STATUSES: string[] = ['completed', 'failed', 'cancelled'];

/** Default candidate page size for the entity-dedup review table. */
export const ENTITY_DEDUP_CANDIDATES_PAGE_SIZE = 100;

/** Page size for the entity-dedup audit table. */
export const ENTITY_DEDUP_AUDIT_PAGE_SIZE = 25;

/** Fallback default for max PDF/DOCX/Markdown upload size in MB.
 * NOT the authoritative limit — the real limit is the org-scoped
 * `orgPdfSizeLimitMb` from PermissionsContext. This constant is consulted
 * only when that value hasn't loaded yet (e.g. ACL still in flight), via
 * `orgPdfSizeLimitMb ?? MAX_PDF_SIZE_MB`. */
export const MAX_PDF_SIZE_MB = 5;

/** Fallback default for max DITA bulk-index ZIP size in MB.
 * NOT the authoritative limit — the real limit is the org-scoped
 * `orgZipSizeLimitMb` from PermissionsContext. This constant is consulted
 * only when that value hasn't loaded yet, via
 * `orgZipSizeLimitMb ?? MAX_BULK_INDEX_ZIP_MB`; 500 matches the backend's
 * default fallback. Upload goes directly to S3 via presigned URL (nginx
 * /s3-upload/ proxy), so the default nginx client_max_body_size does not
 * apply — the /s3-upload/ location has its own 4G ceiling. */
export const MAX_BULK_INDEX_ZIP_MB = 500;

/** File size (bytes) at/above which the bulk-index ZIP upload uses the
 * chunked multipart flow (parallel part PUTs) instead of a single whole-file
 * PUT. 100 MB is safely above S3's 5MB part-size floor and comfortably below
 * where single-PUT throughput starts to matter (see
 * .claude/plans/20260729-multipart-zip-upload-plan.md). */
export const MULTIPART_UPLOAD_THRESHOLD_BYTES = 100 * 1024 * 1024;

/** Max number of part PUTs the multipart upload pool runs in parallel. */
export const MULTIPART_UPLOAD_CONCURRENCY = 5;

/** Max attempts (including the first) for a single part before the upload
 * is treated as unrecoverable and the whole multipart upload is aborted. */
export const MULTIPART_PART_MAX_ATTEMPTS = 3;

/** Base delay (ms) for a part retry's linear backoff (delay = base * attempt). */
export const MULTIPART_PART_RETRY_BASE_DELAY_MS = 1000;

/** File status sort order (errors first) */
export const FILE_STATUS_ORDER: Record<string, number> = {
  ERROR: 0,
  RETRYING: 1,
  DEFERRED: 2,
  PENDING: 3,
  SUCCESS: 4,
};

/** Query param key on /domains/:siteId/chat carrying the active conversation's
 * id, so each distinct chat (or the blank new-chat state) has its own
 * restorable URL and the browser Back button can switch between them.
 * Deliberately NOT `id` — that param is already used on
 * /domains/:siteId/content?id=<docGuid> for an unrelated document deep-link. */
export const CHAT_CONVERSATION_PARAM = 'conversation';

/** Query param key that opens the chat history side panel on /chat. */
export const CHAT_HISTORY_PARAM = 'history';

/** Value of CHAT_HISTORY_PARAM that means "history panel open". */
export const CHAT_HISTORY_PARAM_VALUE = 'true';

/** Top-level application route paths.
 * Use these instead of inlining string literals in `navigate()` calls so a
 * route rename happens in one place. Parametric paths (e.g. domain/site views)
 * still build URLs inline because they need runtime values. */
export const ROUTES = {
  HOME: '/',
  DOCS: '/docs',
  ORG_OVERVIEW: '/org/overview',
  ORG_SECURITY: '/org/security',
  ORG_SAMPLE_CONTENT: '/org/sample-content',
  ORG_MODELS: '/models',
  TEMPLATE_DESIGNER: '/template-designer',
} as const;
